import { FC } from 'react'
import {
  Box,
  Typography,
  Stack,
  Paper,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
} from '@mui/material'
import { BackButton } from '../../components/molecules/BackButton'
import {
  useUserSettingsQuery,
  useUpdateUserSettingsMutation,
} from '../../features/users'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../contexts/ToastContext'
import { useAppType } from '../../contexts/AppTypeContext'
import { isAnnotatorUser, isMediLabApp } from '../../types/appType'
import { localStorageWrapper } from '../../helpers/localStorage'

export const DisplaySettingsPage: FC = () => {
  const { data: settings = null, isPending, isError } = useUserSettingsQuery()
  const updateMutation = useUpdateUserSettingsMutation()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { appType } = useAppType()

  // 新旧 UI の切替は MediLabApp がマウントされる条件下でだけ意味を持つ。
  // apps/medilab/App.tsx の isLegacyUi() 分岐が唯一の分岐先で、annotator / melphin /
  // tmn には latest が存在しない。App.tsx は isAnnotatorUser() を app_type より先に
  // 見るため、app_type だけの判定では annotator を除外できない。
  const canSwitchUi = !isAnnotatorUser() && isMediLabApp(appType)

  if (isPending) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>設定を読み込み中...</Typography>
      </Container>
    )
  }

  if (isError || !settings) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error">設定の読み込みに失敗しました</Typography>
      </Container>
    )
  }

  const handleUseLegacyUiChange = (useLegacy: boolean) => {
    updateMutation.mutate(
      { use_legacy_ui: useLegacy },
      {
        onSuccess: () => {
          // isLegacyUi() は localStorage しか見ないので、ここで同期してから読み込み
          // 直す。check_auth 経由の同期は非同期で、かつ localStorage は再描画を
          // 起こさないため、これが無いと切替が次回の再読み込みまで反映されない。
          localStorageWrapper.setItem('use_legacy_ui', useLegacy)
          window.location.assign('/')
        },
        onError: (err) => {
          console.error('Failed to update use_legacy_ui:', err)
          showToast('画面の切り替えに失敗しました', 'error', 5000)
        },
      }
    )
  }

  const handleDisplayDaysBackChange = (value: number) => {
    updateMutation.mutate(
      { prescription_display_days_back: value },
      {
        onSuccess: () => {
          showToast('表示日数を更新しました', 'success')
        },
        onError: (err) => {
          console.error('Failed to update display days back:', err)
          showToast('表示日数の更新に失敗しました', 'error', 5000)
        },
      }
    )
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Stack spacing={3}>
        {/* 画面表示設定セクション */}
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'medium',
              color: 'primary.dark',
              mb: 3,
              pb: 1,
              borderBottom: '1px solid',
              borderBottomColor: 'primary.dark',
            }}
          >
            画面表示設定
          </Typography>

          <Stack spacing={3}>
            {/* 処方箋表示日数設定 */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'medium', mb: 0.5 }}
                >
                  処方箋表示日数
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  過去何日分の処方箋を表示するかを設定します
                </Typography>
              </Box>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="display-days-back-label">表示日数</InputLabel>
                <Select
                  labelId="display-days-back-label"
                  id="display-days-back-select"
                  value={settings.prescription_display_days_back || 0}
                  label="表示日数"
                  onChange={(e) =>
                    handleDisplayDaysBackChange(Number(e.target.value))
                  }
                >
                  <MenuItem value={0}>当日のみ</MenuItem>
                  {[...Array(7)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1}日前まで
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* 新旧 UI の切替（自己切替）。当社が代行で書き換えず、開放したうえで
                店舗様ご自身に操作していただく口。 */}
            {canSwitchUi && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 'medium', mb: 0.5 }}
                  >
                    新しい画面（試験提供）
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    いつでも元の画面に戻せます
                  </Typography>
                </Box>
                <Switch
                  color="primary"
                  slotProps={{
                    input: { 'aria-label': '新しい画面（試験提供）' },
                  }}
                  checked={!settings.use_legacy_ui}
                  onChange={(e) => handleUseLegacyUiChange(!e.target.checked)}
                  disabled={updateMutation.isPending}
                />
              </Stack>
            )}
          </Stack>
        </Paper>
      </Stack>

      {/* ナビゲーションボタン */}
      <BackButton
        text="処方箋画面へ戻る"
        onClick={() => navigate('/')}
        variant="topLeft"
      />

      <BackButton
        text="管理ポータルに戻る"
        onClick={() => navigate('/portal')}
      />
    </Container>
  )
}

export default DisplaySettingsPage
